import React, {useState, useMemo, useCallback} from "react";
import AccessRightControlPanelComponent from "./AccessRightControlPanel.component";

export default function AccessRightControlPanelContainer({ features, onDataChange, disabled, enabledList = null, layer = 3 }) {
  const removeKeysRecursively = (data, keysToRemove) => {
    if (Array.isArray(data)) {
      return data.map((item) => removeKeysRecursively(item, keysToRemove));
    }

    if (typeof data === "object" && data !== null) {
      return Object.entries(data).reduce((acc, [key, value]) => {
        const newAcc = { ...acc };
        if (!keysToRemove.includes(key)) {
          newAcc[key] = removeKeysRecursively(value, keysToRemove);
        }
        return newAcc;
      }, {});
    }

    return data;
  };

  // Memoize featuresTrimmed to avoid recalculating unless dependencies change
  //
  const featuresTrimmed = useMemo(() =>
    removeKeysRecursively(features, ['module', 'subModules', 'functions', 'actions', 'subActions'].slice(layer, 5))
    // eslint-disable-next-line react-hooks/exhaustive-deps
    , [features, layer]);

  // Memoize initializeEnabledState to avoid unnecessary recalculation
  const initializeEnabledState = useCallback((feats) => {
    const state = {};
    const processItem = (prefix, item) => {
      const itemName = prefix ? `${prefix}.${item.name}` : item.name;
      state[itemName] = enabledList ? enabledList.includes(itemName) : (item.isEnabled ?? false);
      if (item.functions) item.functions.forEach((func) => processItem(itemName, func));
      if (item.actions) item.actions.forEach((action) => processItem(itemName, action));
      if (item.subActions) item.subActions.forEach((subAction) => processItem(itemName, subAction));
      if (item.subModules) item.subModules.forEach((subModule) => processItem(itemName, subModule));
    };
    feats.forEach((module) => processItem('', module));
    return state;
  }, [enabledList]);

  const [enabled, setEnabled] = useState(() => initializeEnabledState(featuresTrimmed));

  // Memoize onChange to avoid unnecessary re-renders
  const onChange = useCallback((name, isChecked, isFunction) => {
    const temp = { ...enabled };
    temp[name] = isChecked;
    const level = isFunction ? 3 : name.split('.').length;
    const updateDescendants = () => {
      Object.keys(temp).forEach((key) => {
        if (key.startsWith(`${name}.`) && key !== name) {
          if (isChecked && key.endsWith('read') && (key.split('.').length === 2 || key.split('.').length === 3)) {
            const writeKey = key.replace('read', 'write');
            if (temp[writeKey] === undefined) {
              temp[key] = isChecked;
            }
          } else {
            temp[key] = isChecked;
          }
        }
      });
    };
    const handleMutuallyExclusiveFunctions = () => {
      if (isFunction) {
        if (name.endsWith('read')) {
          Object.keys(temp).forEach((key) => {
            if (key.startsWith(name.replace('read', 'write'))) {
              temp[key] = false;
            }
          });
        } else {
          Object.keys(temp).forEach((key) => {
            if (key.startsWith(name.replace('write', 'read'))) {
              temp[key] = false;
            }
          });
        }
      }
    };
    const enableAncestors = () => {
      if (isChecked) {
        name.split('.').forEach((_, i) => {
          const ancestorKey = name.split('.').slice(0, i + 1).join('.');
          if (ancestorKey.endsWith('write')) {
            const readKey = ancestorKey.replace('write', 'read');
            if (temp[readKey]) temp[readKey] = false;
          }
          temp[ancestorKey] = true;
        });
      }
    };
    const disableModuleIfSiblingsDisabled = () => {
      if (!isChecked && level === 2) {
        const moduleName = name.split('.')[0];
        const isAllDisabled = Object.keys(temp).filter((key) =>
          key !== moduleName && key.startsWith(moduleName) && temp[key]
        ).length === 0;
        temp[moduleName] = !isAllDisabled;
      }
    };
    updateDescendants();
    handleMutuallyExclusiveFunctions();
    enableAncestors();
    disableModuleIfSiblingsDisabled();
    setEnabled(temp);
    onDataChange(temp);
  }, [enabled, onDataChange]);

  return (
    <AccessRightControlPanelComponent
      features={featuresTrimmed}
      enabled={enabled}
      onChange={onChange}
      disabled={disabled}
      layer={layer}
    />
  );
}
